import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  IConciergeSettingsResponse,
  IConciergeSettingsUpdateRequest,
  IConciergeSettingsData,
  IKnowledgeDocumentCategoriesResponse,
  IKnowledgeDocumentStatusesResponse,
  IKnowledgeDocumentsByCustomerResponse,
  IUploadKnowledgeDocumentResponse,
  IUploadKnowledgeDocumentRequest,
  IDeleteKnowledgeDocumentResponse,
  IUpdateKnowledgeDocumentStatusRequest,
  IUpdateKnowledgeDocumentStatusResponse,
  IWabaListResponse,
  IKnowledgeProposedChangesResponse,
  IKnowledgeAddItem,
  IAddKnowledgeItemResponse,
  IKnowledgeBaseTraficLightResponse,
  ILineSettingsResponse,
  IMessagesWithoutKnowledgeResponse,
  IApiResponse,
} from '../model/concierge-settings.interface';
import {
  ICampaignsByCustomerResponse,
  ICustomerWspCampaignTemplate,
  IWhatsappTemplatePreviewResponse,
  IWhatsappTemplateTestResponse,
  IWhatsappCalculatedBagsResponse,
  WspMessageBag,
  IRequestWhatsappBagResponse,
  ICampaignUsageResponse,
} from '../model/concierge-campaigns.interface';
import { share, retry, map, switchMap, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConciergeConfigService {
  private readonly knowledgeDocsUrl = `${environment.conciergePath}knowledge-documents`;
  private readonly conciergeUrl = `${environment.conciergePath}concierges`;

  private conciergeSettingsMap: Map<number, IConciergeSettingsData> = new Map();

  private emitterData$: Observable<any>;
  private stopPolling = new Subject<void>();
  private webScrapingEmitterData$: Observable<any>;
  private stopWebScrapingPolling = new Subject<void>();
  public pollingInterval = 15000;

  constructor(private http: HttpClient) {}

  async getConciergeSettings(customerId: number): Promise<IConciergeSettingsData> {
    return new Promise((resolve, reject) => {
      this.loadConciergeSettings(customerId).subscribe(
        (response: IConciergeSettingsResponse) => {
          this.setStoredConciergeSettings(customerId, response.data);
          resolve(response.data);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  // Knowledge Documents - PDF Upload
  uploadKnowledgeDocument(request: IUploadKnowledgeDocumentRequest): Observable<IUploadKnowledgeDocumentResponse> {
    const formData = this.buildFormData(request);
    return this.http.post<IUploadKnowledgeDocumentResponse>(this.knowledgeDocsUrl, formData);
  }

  // Scrape URL
  scrapeUrl(url: string, customerId: number): Observable<IUploadKnowledgeDocumentResponse> {
    const body = {
      url: url,
      customer_id: customerId,
    };
    return this.http.post<IUploadKnowledgeDocumentResponse>(`${this.knowledgeDocsUrl}/scrape`, body);
  }

  private buildFormData(request: IUploadKnowledgeDocumentRequest): FormData {
    const formData = new FormData();
    formData.append('file', request.file);

    const data: any = {
      customer_id: request.customerId,
      knowledge_type_id: request.knowledgeTypeId,
      knowledge_status_id: request.knowledgeStatusId,
      public: request.isPublic || false,
      nomenclator: 'KNOWLEDGE_BASE',
    };

    if (request.knowledgeCategoryId) {
      data.knowledge_category_id = request.knowledgeCategoryId;
    }

    if (request?.description?.trim() !== '') {
      data.description = request.description;
    }

    const dataBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', dataBlob);

    return formData;
  }

  getKnowledgeDocumentsByCustomer(
    customerId: number,
    knowledgeTypeId?: number,
  ): Observable<IKnowledgeDocumentsByCustomerResponse> {
    let url = `${this.knowledgeDocsUrl}/customer/${customerId}`;
    if (knowledgeTypeId !== undefined) {
      url += `?knowledge_type_id=${knowledgeTypeId}`;
    }
    return this.http.get<IKnowledgeDocumentsByCustomerResponse>(url);
  }

  deleteKnowledgeDocument(documentId: number): Observable<IDeleteKnowledgeDocumentResponse> {
    return this.http.delete<IDeleteKnowledgeDocumentResponse>(`${this.knowledgeDocsUrl}/${documentId}`);
  }

  updateKnowledgeDocumentStatus(
    documentId: number,
    request: IUpdateKnowledgeDocumentStatusRequest,
  ): Observable<IUpdateKnowledgeDocumentStatusResponse> {
    return this.http.patch<IUpdateKnowledgeDocumentStatusResponse>(
      `${this.knowledgeDocsUrl}/${documentId}/status`,
      request,
    );
  }

  updateKnowledgeDocumentDescription(
    documentId: number,
    description: string,
  ): Observable<{ data: boolean; errors: any[]; status: string }> {
    return this.http.patch<{ data: boolean; errors: any[]; status: string }>(
      `${this.knowledgeDocsUrl}/${documentId}/description`,
      { description },
    );
  }

  getKnowledgeDocumentCategories(): Observable<IKnowledgeDocumentCategoriesResponse> {
    return this.http.get<IKnowledgeDocumentCategoriesResponse>(`${this.knowledgeDocsUrl}/categories`);
  }

  getKnowledgeDocumentStatuses(): Observable<IKnowledgeDocumentStatusesResponse> {
    return this.http.get<IKnowledgeDocumentStatusesResponse>(`${this.knowledgeDocsUrl}/statuses`);
  }

  // Concierge Settings
  getConciergeSettingsByCustomer(customerId: number): Observable<IConciergeSettingsResponse> {
    return this.http.get<IConciergeSettingsResponse>(`${this.conciergeUrl}/customer/${customerId}/settings`);
  }

  loadConciergeSettings(customerId: number): Observable<IConciergeSettingsResponse> {
    return this.http.get<IConciergeSettingsResponse>(`${this.conciergeUrl}/customer/${customerId}/settings`);
  }

  updateConciergeSettings(
    customerId: number,
    settings: IConciergeSettingsUpdateRequest,
  ): Observable<IConciergeSettingsResponse> {
    return this.http.post<IConciergeSettingsResponse>(`${this.conciergeUrl}/customer/${customerId}/settings`, settings);
  }

  saveConciergeSettings(
    customerId: number,
    settings: IConciergeSettingsUpdateRequest,
  ): Observable<IConciergeSettingsResponse> {
    return this.http.post<IConciergeSettingsResponse>(`${this.conciergeUrl}/customer/${customerId}/settings`, settings);
  }

  updateGuestsRequestsNotifications(
    customerId: number,
    body: {
      notify_guest_service_request: boolean;
      confirm_status_change: boolean;
      hide_resolved_requests: boolean;
    },
  ): Observable<IApiResponse<boolean>> {
    return this.http.patch<IApiResponse<boolean>>(
      `${this.conciergeUrl}/customer/${customerId}/service-request-config`,
      body,
    );
  }

  getConversationStats(customer_id: number, params: any) {
    return this.http.get<any>(`${environment.conciergePath}/customers/${customer_id}/conversations/stats`, {
      params,
    });
  }

  getConversationGuestsStats(customer_id: number, params: any) {
    return this.http.get<any>(`${environment.conciergePath}/customers/${customer_id}/conversations/guests/stats`, {
      params,
    });
  }

  getAverageFirstResponse(customer_id: number, params: any) {
    return this.http.get<any>(`${environment.conciergePath}/customers/conversations/average-first-response`, {
      params: { ...params, customer_ids: customer_id },
    });
  }

  getConversationSummaryTypeTrend(customer_id: number, params: any) {
    return this.http.get<any>(
      `${environment.conciergePath}/customers/${customer_id}/conversations/summary/type/trend`,
      {
        params,
      },
    );
  }

  getStoredConciergeSettings(customerId: number): IConciergeSettingsData | null {
    return this.conciergeSettingsMap.get(customerId) || null;
  }

  setStoredConciergeSettings(customerId: number, settings: IConciergeSettingsData): void {
    this.conciergeSettingsMap.set(customerId, settings);
  }

  clearStoredConciergeSettings(customerId: number): void {
    this.conciergeSettingsMap.delete(customerId);
  }

  clearAllStoredConciergeSettings(): void {
    this.conciergeSettingsMap.clear();
  }

  getStoredCustomerIds(): number[] {
    return Array.from(this.conciergeSettingsMap.keys());
  }

  getChatLineConfig(conciergeId: number): Observable<any> {
    return this.http.get<any>(`${environment.conciergePath}concierges/${conciergeId}/chat-line`);
  }

  getChatLineConfigByCustomer(customerId: number): Observable<ILineSettingsResponse> {
    return this.http.get<ILineSettingsResponse>(
      `${environment.conciergePath}concierges/customer/${customerId}/chat-line`,
    );
  }

  updateChatLineConfig(conciergeId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${environment.conciergePath}concierges/${conciergeId}/chat-line`, payload);
  }

  getWabaList() {
    return this.http.get<IWabaListResponse>(`${environment.conciergePath}wabas`);
  }

  // Knowledge Base

  getKnowledgeSections(customerId: number): Observable<any> {
    return this.http.get<any>(`${environment.conciergePath}knowledge-sections/customer/${customerId}`);
  }

  getKnowledgeProposedChanges(customerId: number): Observable<IKnowledgeProposedChangesResponse> {
    return this.http.get<IKnowledgeProposedChangesResponse>(
      `${environment.conciergePath}knowledge-master/customer/${customerId}/proposed-changes`,
    );
  }

  bulkUpsertKnowledgeSections(payload: any): Observable<any> {
    return this.http.put<any>(`${environment.conciergePath}knowledge-sections/bulk-upsert`, payload);
  }

  updateKnowledgeItem(itemId: number, payload: any): Observable<any> {
    const params = payload.skip_formatting ? { skip_formatting: 'true' } : {};
    return this.http.patch<any>(`${environment.conciergePath}knowledge-section-items/${itemId}`, payload, { params });
  }

  deleteKnowledgeItem(itemId: number): Observable<any> {
    return this.http.delete<any>(`${environment.conciergePath}knowledge-section-items/${itemId}`);
  }

  searchKnowledgeSectionItems(customerId: number, query: string, topK = 10): Observable<any> {
    const params = {
      customer_id: customerId.toString(),
      query: query,
      top_k: topK.toString(),
    };
    return this.http.get<any>(`${environment.conciergePath}knowledge-section-items/search`, { params });
  }

  createNewKnowledgeItem(payload: IKnowledgeAddItem): Observable<IAddKnowledgeItemResponse> {
    const params = payload.skip_formatting ? { skip_formatting: 'true' } : {};
    return this.http.post<IAddKnowledgeItemResponse>(`${environment.conciergePath}knowledge-section-items`, payload, {
      params,
    });
  }

  getKnowledgeBaseTraficLight(
    customerId: number,
    nomenclator = 'KNOWLEDGE_BASE',
  ): Observable<IKnowledgeBaseTraficLightResponse> {
    const url = `${environment.conciergePath}traffic-light/customer/${customerId}/nomenclator/${nomenclator}`;

    return this.http.get<IKnowledgeBaseTraficLightResponse>(url);
  }

  initKnowledgeBaseTraficLightPolling(customerId: number) {
    this.emitterData$ = timer(1, this.pollingInterval).pipe(
      switchMap(() => this.getKnowledgeBaseTraficLight(customerId)),
      map((res) => (res ? res : [])),
      retry(3),
      share(),
      takeUntil(this.stopPolling),
    );

    return this.emitterData$;
  }

  destroyKnowledgeBaseTraficLightPolling() {
    this.stopPolling.next();
  }

  initWebScrapingTraficLightPolling(customerId: number) {
    this.webScrapingEmitterData$ = timer(1, this.pollingInterval).pipe(
      switchMap(() => this.getKnowledgeBaseTraficLight(customerId, 'WEB_SCRAPING')),
      map((res) => (res ? res : [])),
      retry(3),
      share(),
      takeUntil(this.stopWebScrapingPolling),
    );

    return this.webScrapingEmitterData$;
  }

  destroyWebScrapingTraficLightPolling() {
    this.stopWebScrapingPolling.next();
    this.stopWebScrapingPolling = new Subject<void>();
  }

  // Campaigns
  getCampaignsByCustomer(customerId: number): Observable<ICampaignsByCustomerResponse> {
    return this.http.get<ICampaignsByCustomerResponse>(`${environment.conciergePath}customers/${customerId}/campaigns`);
  }

  getWhatsappTemplatePreview(
    campaignId: number,
    templateId: number,
    languageId: number,
  ): Observable<IWhatsappTemplatePreviewResponse> {
    const params = {
      template_id: templateId,
      language_id: languageId,
    };
    return this.http.get<IWhatsappTemplatePreviewResponse>(
      `${environment.conciergePath}campaigns/${campaignId}/whatsapp/template/preview`,
      { params },
    );
  }

  sendWhatsappTemplateTest(
    campaignId: number,
    templateId: number,
    phoneNumber: string,
    languageId: number,
  ): Observable<IWhatsappTemplateTestResponse> {
    const params = {
      template_id: templateId,
      phone_number: phoneNumber,
      language_id: languageId,
    };
    return this.http.post<IWhatsappTemplateTestResponse>(
      `${environment.conciergePath}campaigns/${campaignId}/whatsapp/template/test`,
      null,
      { params },
    );
  }

  updateCampaignConfig(
    customerId: number,
    campaignData: ICustomerWspCampaignTemplate,
  ): Observable<ICampaignsByCustomerResponse> {
    return this.http.post<ICampaignsByCustomerResponse>(
      `${environment.conciergePath}customers/${customerId}/campaigns`,
      campaignData,
    );
  }

  getCalculatedBags(customerId: number, selectedCampaigns: number): Observable<IWhatsappCalculatedBagsResponse> {
    const params = {
      campaigns_selected: selectedCampaigns,
    };

    return this.http.get<IWhatsappCalculatedBagsResponse>(
      `${environment.conciergePath}customers/${customerId}/wsp-message-bags`,
      { params },
    );
  }

  requestWhatsappBag(customerId: number, bag: WspMessageBag): Observable<IRequestWhatsappBagResponse> {
    return this.http.post<IRequestWhatsappBagResponse>(
      `${environment.conciergePath}customers/${customerId}/wsp-message-bags`,
      bag,
    );
  }

  getCampaignBagsRequested(customerId: number): Observable<IRequestWhatsappBagResponse> {
    return this.http.get<IRequestWhatsappBagResponse>(
      `${environment.conciergePath}customers/${customerId}/wsp-message-bag-requests`,
    );
  }

  getCampaignUsage(customerId: number): Observable<ICampaignUsageResponse> {
    return this.http.get<ICampaignUsageResponse>(
      `${environment.conciergePath}customers/${customerId}/campaigns/monthly-usage`,
    );
  }

  canActivateDedicatedLine(customerId: number): Observable<{ data: boolean; errors: any[]; status: string }> {
    return this.http.get<{ data: boolean; errors: any[]; status: string }>(
      `${environment.conciergePath}customers/${customerId}/line/can-activate`,
    );
  }

  sendDemoNotification(customerId: number): Observable<any> {
    return this.http.post<any>(`${this.conciergeUrl}/demo/notification`, { customer_id: customerId });
  }

  testConciergeDemo(customerId: number, phoneNumber: string): Observable<any> {
    return this.http.post<any>(`${this.conciergeUrl}/demo`, { customer_id: customerId, phone_number: phoneNumber });
  }

  getMessagesWithoutKnowledge(
    customerId: number,
    startDate: string,
    endDate: string,
    page: number = 0,
    size: number = 10,
  ): Observable<IMessagesWithoutKnowledgeResponse> {
    const params = {
      start_date: startDate,
      end_date: endDate,
      page: page.toString(),
      size: size.toString(),
    };
    return this.http.get<IMessagesWithoutKnowledgeResponse>(
      `${environment.conciergePath}customers/${customerId}/messages/without-knowledge`,
      { params },
    );
  }

  markMessageKnowledgeFound(customerId: number, chatMessageId: number): Observable<IApiResponse<boolean>> {
    return this.http.patch<IApiResponse<boolean>>(
      `${environment.conciergePath}customers/${customerId}/messages/${chatMessageId}/knowledge-found`,
      {},
    );
  }
}
